import type { Invoice } from '@/types/invoice';

export type BillToSnapshot = NonNullable<Invoice['billToContact']>;

/** A null `billToId` means the invoice bills the direct contact (Self). */
export function isSelfBillTo(snapshot?: BillToSnapshot | null): boolean {
  return !snapshot?.billToId;
}

/** The resolved person/party name. "Self" is only a last-resort fallback. */
export function billToDisplayName(snapshot?: BillToSnapshot | null): string {
  if (!snapshot) return '';
  const name = snapshot.contactName ?? snapshot.name ?? '';
  if (name) return snapshot.billToId ? [snapshot.label, name].filter(Boolean).join(' · ') : name;
  return snapshot.label ?? 'Self';
}

/**
 * Recipient email for sending/charging an invoice. Any resolved snapshot wins,
 * including an additional person selected under Self. Only a missing snapshot
 * email falls back to the direct contact.
 */
export function billToRecipientEmail(
  snapshot?: BillToSnapshot | null,
  fallbackEmail?: string | null,
): string {
  if (snapshot?.email) return snapshot.email;
  return fallbackEmail ?? '';
}

/**
 * Recipient phone for texting an invoice. Any resolved snapshot wins; a missing
 * snapshot phone falls back to the direct contact.
 */
export function billToRecipientPhone(
  snapshot?: BillToSnapshot | null,
  fallbackPhone?: string | null,
  fallbackContactId?: string | null,
  resolvedBillToPhone?: string | null,
): string {
  if (snapshot?.phone) return snapshot.phone;
  // A distinct selected bill-to contact must never inherit the invoice
  // owner's phone. Let the caller resolve that contact's live phone instead.
  if (snapshot?.contactId && snapshot.contactId !== fallbackContactId) {
    return resolvedBillToPhone ?? '';
  }
  return fallbackPhone ?? '';
}

/** Resolve an invoice recipient without accidentally falling back to the owner
 * when a different bill-to contact was selected. */
export function invoiceBillToRecipientPhone(
  invoice: Invoice,
  resolvedBillToPhone?: string | null,
): string {
  return billToRecipientPhone(
    invoice.billToContact,
    invoice.contact?.primaryPhone,
    invoice.contact?.id ?? invoice.contactId,
    resolvedBillToPhone,
  );
}

/** One-line billing address from the snapshot (override already applied by the resolver). */
export function billToAddressLine(snapshot?: BillToSnapshot | null): string {
  const address = snapshot?.billingAddress;
  if (!address) return '';
  const cityLine = [address.city, address.state].filter(Boolean).join(', ');
  return [
    address.street1,
    address.street2,
    [cityLine, address.postalCode].filter(Boolean).join(' '),
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

/** One-line address from the invoice's final billing-address snapshot. */
export function invoiceBillingAddressLine(
  address?: NonNullable<Invoice['billingAddress']> | null,
): string {
  if (!address) return '';
  const cityLine = [address.city, address.state].filter(Boolean).join(', ');
  return [
    address.street1 ?? address.street,
    address.street2,
    [cityLine, address.postalCode ?? address.zip].filter(Boolean).join(' '),
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * The address a document prints under "Bill To".
 *
 * The invoice carries its OWN billing-address snapshot, copied from the job
 * independently of the bill-to contact, so a one-off address typed on this
 * invoice exists only there. It wins; the contact's stands in when the invoice
 * has none. The detail panel and the customer's copy already worked this way
 * and the two office PDFs did not, which printed a stale address for the
 * office while the customer got the right one.
 */
export function invoiceBillToAddressLine(
  invoice: Pick<Invoice, 'billingAddress'>,
  billTo?: BillToSnapshot | null,
): string {
  return invoiceBillingAddressLine(invoice.billingAddress) || billToAddressLine(billTo);
}
